import React, { useEffect, useState, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import Header from "./Header";
import Footer from "./Footer";
import API_BASE_URL from "../Config";
import "./styles/Banner.css";
import "./styles/Reviews.css";
import "../styles/global.css";

/* ────────── Outline SVG Icons ────────── */
const Icon = ({ d, size = 18, stroke = 1.8, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    {d}
  </svg>
);

const SearchIcon = (p) => (
  <Icon {...p} d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />
);
const CloseIcon = (p) => (
  <Icon {...p} d={<><path d="M18 6 6 18M6 6l12 12" /></>} />
);
const TagIcon = (p) => (
  <Icon {...p} d={<><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></>} />
);
const StarIcon = (p) => (
  <Icon {...p} d={<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />} />
);
const ChevronLeftIcon = (p) => (
  <Icon {...p} d={<><path d="m15 18-6-6 6-6" /></>} />
);
const ChevronRightIcon = (p) => (
  <Icon {...p} d={<><path d="m9 18 6-6-6-6" /></>} />
);

/* ────────── iPhone-style Toggle (same convention as DoctorList's status toggle) ────────── */
const ToggleSwitch = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={`rv-toggle ${checked ? "is-on" : "is-off"}`}
  >
    <span className="rv-toggle__thumb" />
  </button>
);

/* ────────── Custom Confirm Dialog ────────── */
const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true" aria-labelledby="cdlg-title">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h3 id="cdlg-title" className="cdlg-title">{title}</h3>
        <p className="cdlg-message">{message}</p>
        <div className="cdlg-actions">
          <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

/* ────────── Custom Pagination ────────── */
const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach((i) => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  return (
    <div className="bn-pagination">
      <div className="bn-pagination__info">
        <span>
          Showing <b>{totalRows === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalRows)}</b>–
          <b>{Math.min(currentPage * perPage, totalRows)}</b> of <b>{totalRows}</b>
        </span>
        <select className="bn-pagination__perpage" value={perPage} onChange={(e) => onPerPageChange(Number(e.target.value))}>
          {[10, 25, 50, 100].map((n) => (<option key={n} value={n}>{n} / page</option>))}
        </select>
      </div>
      <div className="bn-pagination__pages">
        <button className="bn-pagination__btn" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)} aria-label="Previous page">
          <ChevronLeftIcon size={16} />
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`e-${i}`} className="bn-pagination__ellipsis">…</span>
          ) : (
            <button key={p} className={`bn-pagination__btn ${p === currentPage ? "is-active" : ""}`} onClick={() => onPageChange(p)} aria-current={p === currentPage ? "page" : undefined}>
              {p}
            </button>
          )
        )}
        <button className="bn-pagination__btn" disabled={currentPage === totalPages || totalPages === 0} onClick={() => onPageChange(currentPage + 1)} aria-label="Next page">
          <ChevronRightIcon size={16} />
        </button>
      </div>
    </div>
  );
};

/* ────────── Component ────────── */
const Reviews = () => {
  const [loading, setLoading] = useState(false);
  const [productReviews, setProductReviews] = useState([]);
  const [doctorReviews, setDoctorReviews] = useState([]);
  const [activeTab, setActiveTab] = useState("products"); // "products" | "doctors"
  const [filterText, setFilterText] = useState("");

  const [messagealert, setMessageAlert] = useState("");
  const [messageType, setMessageType] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [togglingId, setTogglingId] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false, title: "", message: "", confirmLabel: "Confirm", confirmVariant: "warning", onConfirm: null,
  });

  const showConfirm = useCallback(({ title, message, onConfirm, confirmLabel = "Confirm", confirmVariant = "warning" }) => {
    setConfirmDialog({ open: true, title, message, onConfirm, confirmLabel, confirmVariant });
  }, []);
  const closeConfirm = useCallback(() => {
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
  }, []);

  const token = localStorage.getItem("authToken");

  const flashMessage = (msg, type = "success") => {
    setMessageAlert(msg);
    setMessageType(type);
    setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4000);
  };

  /* ── Fetch reviews (both product & doctor lists come back together) ── */
  const fetchReviews = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/reviews`, {
      method: "GET",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          setProductReviews(data.data.products || []);
          setDoctorReviews(data.data.doctors || []);
        } else {
          console.error("Unexpected response format:", data);
        }
      })
      .catch((err) => console.error("Error fetching reviews:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchReviews(); }, []);
  useEffect(() => { setCurrentPage(1); }, [filterText, activeTab]);

  const rows = activeTab === "products" ? productReviews : doctorReviews;

  const filteredRows = useMemo(() => {
    if (!filterText) return rows;
    const ft = filterText.toLowerCase();
    return rows.filter(
      (row) =>
        row.target_name?.toLowerCase().includes(ft) ||
        row.reviewer?.toLowerCase().includes(ft) ||
        row.comment?.toLowerCase().includes(ft) ||
        row.id?.toString().includes(ft)
    );
  }, [rows, filterText]);

  const totalRows = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalRows, perPage, currentPage, totalPages]);

  const pagedData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredRows.slice(start, start + perPage);
  }, [filteredRows, currentPage, perPage]);

  const truncate = (str, len = 70) => {
    if (!str) return "-";
    return str.length > len ? str.slice(0, len) + "…" : str;
  };

  /* ── Toggle Active / Inactive ── */
  const handleToggleStatus = (row) => {
    const newStatus = row.status === "Active" ? "Inactive" : "Active";
    showConfirm({
      title: newStatus === "Active" ? "Approve Review" : "Hide Review",
      message:
        newStatus === "Active"
          ? "This review will become visible on the public page. Continue?"
          : "This review will be hidden from the public page. Continue?",
      confirmLabel: newStatus === "Active" ? "Yes, Approve" : "Yes, Hide",
      confirmVariant: newStatus === "Active" ? "success" : "warning",
      onConfirm: () => {
        closeConfirm();
        setTogglingId(row.id);
        fetch(`${API_BASE_URL}api/reviews/status/${row.type}/${row.id}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage(`Review status updated to ${newStatus}`, "success");
              fetchReviews();
            } else {
              flashMessage("Failed to update status.", "danger");
            }
          })
          .catch(() => flashMessage("Error updating status.", "danger"))
          .finally(() => setTogglingId(null));
      },
    });
  };

  /* ────────── Table Columns ────────── */
  const columns = useMemo(
    () => [
      {
        name: "ID",
        cell: (row) => <span className="id-pill">#{row.id}</span>,
        width: "80px",
        sortable: true,
        selector: (row) => row.id,
      },
      {
        name: activeTab === "products" ? "Product" : "Doctor",
        selector: (row) => row.target_name,
        cell: (row) => <span className="cell-name">{row.target_name}</span>,
        sortable: true,
        grow: 1.4,
      },
      {
        name: "Reviewer",
        selector: (row) => row.reviewer,
        cell: (row) => <span className="cell-muted">{row.reviewer}</span>,
        sortable: true,
      },
      {
        name: "Rating",
        selector: (row) => row.rating,
        cell: (row) => (
          <span className="rv-stars" title={`${row.rating} / 5`}>
            {[1, 2, 3, 4, 5].map((n) => (
              <StarIcon
                key={n}
                size={14}
                stroke={0}
                className={n <= row.rating ? "rv-star-filled" : "rv-star-empty"}
              />
            ))}
          </span>
        ),
        width: "140px",
      },
      {
        name: "Comment",
        cell: (row) => <span className="cell-muted">{truncate(row.comment)}</span>,
        grow: 2,
      },
      {
        name: "Submitted",
        selector: (row) => row.created_at,
        cell: (row) => (
          <span className="cell-muted">
            {row.created_at ? new Date(row.created_at).toLocaleDateString("en-IN") : "-"}
          </span>
        ),
        sortable: true,
      },
      {
        name: "Status",
        cell: (row) => (
          <ToggleSwitch
            checked={row.status === "Active"}
            disabled={togglingId === row.id}
            onChange={() => handleToggleStatus(row)}
          />
        ),
        width: "110px",
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTab, togglingId]
  );

  const tableStyles = {
    table: { style: { background: "transparent" } },
    headRow: { style: { background: "#f0fbf6", borderTopLeftRadius: "12px", borderTopRightRadius: "12px", borderBottom: "1px solid #d6efe2", minHeight: "52px" } },
    headCells: { style: { fontSize: "12px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#5b6b66", paddingLeft: "16px", paddingRight: "16px" } },
    rows: { style: { minHeight: "72px", fontSize: "14px", color: "#1f2a37", borderBottom: "1px solid #eef4f1 !important", transition: "background 0.18s ease", "&:hover": { background: "#f6fcf9" } }, stripedStyle: { background: "#fafefb" } },
    cells: { style: { paddingLeft: "16px", paddingRight: "16px" } },
    pagination: { style: { display: "none" } },
  };

  return (
    <>
      <div id="layout-wrapper">
        <Header />
        {loading && (
          <div className="bn-overlay"><div className="bn-spinner" /></div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="banner-shell">

              <div className="banner-head">
                <div>
                  <h2>Reviews</h2>
                  <p>Approve or hide reviews submitted by customers for products and doctors. New reviews start Inactive until approved here.</p>
                </div>
              </div>

              <div className="banner-card">
                <div className="rv-tabs">
                  <button
                    type="button"
                    className={`rv-tab ${activeTab === "products" ? "is-active" : ""}`}
                    onClick={() => setActiveTab("products")}
                  >
                    Product Reviews
                    <span className="rv-tab-count">{productReviews.length}</span>
                  </button>
                  <button
                    type="button"
                    className={`rv-tab ${activeTab === "doctors" ? "is-active" : ""}`}
                    onClick={() => setActiveTab("doctors")}
                  >
                    Doctor Reviews
                    <span className="rv-tab-count">{doctorReviews.length}</span>
                  </button>
                </div>

                <div className="card-top">
                  <div className="search-box">
                    <SearchIcon size={18} />
                    <input
                      type="text"
                      placeholder={activeTab === "products" ? "Search by product, reviewer, comment…" : "Search by doctor, reviewer, comment…"}
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                  </div>
                  <div className="total-badge">
                    <TagIcon size={16} />
                    Total:&nbsp;<b>{filteredRows.length}</b>
                  </div>
                </div>

                {messagealert && (
                  <div className={`bn-alert ${messageType}`} role="alert">
                    <span>{messagealert}</span>
                    <button type="button" onClick={() => setMessageAlert("")} aria-label="Close"><CloseIcon size={16} /></button>
                  </div>
                )}

                <DataTable
                  columns={columns}
                  data={pagedData}
                  customStyles={tableStyles}
                  highlightOnHover
                  responsive
                  noDataComponent={
                    <div style={{ padding: "40px 20px", color: "var(--bn-muted)", textAlign: "center" }}>
                      No {activeTab === "products" ? "product" : "doctor"} reviews found.
                    </div>
                  }
                />

                {filteredRows.length > 0 && (
                  <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    perPage={perPage}
                    totalRows={totalRows}
                    onPerPageChange={(n) => { setPerPage(n); setCurrentPage(1); }}
                  />
                )}
              </div>
            </div>
          </div>

          <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmLabel={confirmDialog.confirmLabel}
            confirmVariant={confirmDialog.confirmVariant}
            onConfirm={confirmDialog.onConfirm}
            onCancel={closeConfirm}
          />

          <Footer />
        </div>
      </div>
    </>
  );
};

export default Reviews;
